import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as SelectedIcon,
  InfoOutlined as InfoIcon,
  Person as PersonIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import PatientProfileAvatar from '../PatientProfileAvatar';
import { listMetahumanCharacterOptions } from '../../utils/userContentApi';
import { loadPatientProfilePictureFull } from '../../utils/patientProfilePicture';
import {
  asText,
  detailLines,
  filterCharacterOptions,
} from '../../utils/metahumanCharacterSearch';

function useLazyInView(scrollRootRef) {
  const targetRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return undefined;
    const target = targetRef.current;
    const root = scrollRootRef?.current ?? null;
    if (!target) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { root, rootMargin: '240px 0px', threshold: 0.01 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [scrollRootRef, inView]);

  return [targetRef, inView];
}

function CharacterDetailsOverlay({ option, open }) {
  const lines = detailLines(option);
  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        p: 1,
        color: 'common.white',
        '& .MuiTypography-root': { color: 'common.white' },
        background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.72) 55%, rgba(0,0,0,0.35) 100%)',
        animation: 'characterInfoIn 0.28s ease',
        '@keyframes characterInfoIn': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
        overflowY: 'auto',
      }}
    >
      {lines.length === 0 ? (
        <Typography variant="caption" sx={{ color: 'common.white' }}>
          No extra details on file.
        </Typography>
      ) : (
        lines.map((line) => (
          <Typography
            key={line.label}
            variant="caption"
            component="p"
            sx={{ m: 0, mb: 0.35, lineHeight: 1.35, color: 'common.white' }}
          >
            <Box component="span" sx={{ fontWeight: 600, color: 'common.white' }}>
              {line.label}
              :
            </Box>
            {' '}
            {line.value}
          </Typography>
        ))
      )}
    </Box>
  );
}

function CharacterTile({
  option,
  selected,
  onSelect,
  scrollRootRef,
  pinnedInfoIds,
  onToggleInfoPin,
}) {
  const [tileRef, inView] = useLazyInView(scrollRootRef);
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const [cardHovered, setCardHovered] = useState(false);
  const [infoHovered, setInfoHovered] = useState(false);

  const characterId = String(option?.characterId ?? '');
  const isSelected = selected === characterId && characterId !== '';
  const isPinned = pinnedInfoIds.has(characterId);
  const detailsOpen = isPinned || infoHovered;
  const showInfoControl = cardHovered || isPinned || infoHovered;

  useEffect(() => {
    if (!inView || !option?.hasProfilePicture || !option.personaId) {
      if (!inView) {
        setSrc(null);
        setFailed(false);
      }
      return undefined;
    }
    let cancelled = false;
    loadPatientProfilePictureFull(option.personaId, option.profilePictureRevision)
      .then((url) => {
        if (!cancelled) {
          setSrc(url || null);
          setFailed(!url);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSrc(null);
          setFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [inView, option?.hasProfilePicture, option?.personaId, option?.profilePictureRevision]);

  const displayName = option?.name?.trim() || 'Library character';

  return (
    <ImageListItem
      sx={{
        cursor: 'pointer',
        borderRadius: 1,
        overflow: 'hidden',
        outline: isSelected ? '3px solid' : '1px solid',
        outlineColor: isSelected ? 'primary.main' : 'divider',
        outlineOffset: isSelected ? 0 : -1,
      }}
      onMouseEnter={() => setCardHovered(true)}
      onMouseLeave={() => {
        setCardHovered(false);
        if (!isPinned) setInfoHovered(false);
      }}
      onClick={() => onSelect(characterId)}
    >
      <Box
        ref={tileRef}
        sx={{
          position: 'relative',
          width: '100%',
          pt: '125%',
          bgcolor: 'grey.100',
        }}
      >
        {src ? (
          <Box
            component="img"
            src={src}
            alt=""
            loading="lazy"
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: failed ? 'text.disabled' : 'text.secondary',
            }}
          >
            {inView && option?.hasProfilePicture ? (
              <CircularProgress size={24} />
            ) : (
              <PersonIcon sx={{ fontSize: 40, opacity: 0.35 }} />
            )}
          </Box>
        )}

        <CharacterDetailsOverlay option={option} open={detailsOpen} />

        {showInfoControl && (
          <Tooltip title={isPinned ? 'Unpin details' : 'Pin details'}>
            <IconButton
              size="small"
              aria-label={isPinned ? 'Unpin character details' : 'Show character details'}
              aria-pressed={isPinned}
              onMouseEnter={() => setInfoHovered(true)}
              onMouseLeave={() => {
                if (!isPinned) setInfoHovered(false);
              }}
              onClick={(event) => {
                event.stopPropagation();
                onToggleInfoPin(characterId);
              }}
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                left: 'auto',
                zIndex: 3,
                bgcolor: 'rgba(255,255,255,0.92)',
                opacity: showInfoControl ? 1 : 0,
                transition: 'opacity 0.2s ease',
                '&:hover': { bgcolor: 'common.white' },
              }}
            >
              <InfoIcon fontSize="small" color={isPinned ? 'primary' : 'action'} />
            </IconButton>
          </Tooltip>
        )}

        {isSelected && (
          <SelectedIcon
            color="primary"
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              right: 'auto',
              bgcolor: 'background.paper',
              borderRadius: '50%',
              zIndex: 3,
            }}
          />
        )}
      </Box>
      <ImageListItemBar
        title={displayName}
        sx={{
          '& .MuiImageListItemBar-title': {
            fontSize: '0.75rem',
            lineHeight: 1.2,
          },
        }}
      />
    </ImageListItem>
  );
}

function SelectedCharacterPreview({ option, onClear }) {
  const [panelHovered, setPanelHovered] = useState(false);
  if (!option) return null;
  const persona = {
    id: option.personaId,
    has_profile_picture: option.hasProfilePicture,
    profile_picture_revision: option.profilePictureRevision,
  };
  const summary = detailLines(option)
    .map((line) => `${line.label}: ${line.value}`)
    .join(' · ');
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="flex-start"
      onMouseEnter={() => setPanelHovered(true)}
      onMouseLeave={() => setPanelHovered(false)}
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.50',
      }}
    >
      <PatientProfileAvatar persona={persona} size={72} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" noWrap>
          {option.name?.trim() || 'Library character'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          Selected for the simulation scene.
        </Typography>
        {summary ? (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
            {summary}
          </Typography>
        ) : null}
      </Box>
      <Button
        type="button"
        variant="outlined"
        size="small"
        onClick={onClear}
        sx={{
          flexShrink: 0,
          textTransform: 'none',
          opacity: panelHovered ? 1 : 0,
          transition: 'opacity 0.2s ease',
          pointerEvents: panelHovered ? 'auto' : 'none',
        }}
      >
        Clear selection
      </Button>
    </Stack>
  );
}

export default function MetahumanCharacterPicker({ value, onChange, error }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [pinnedInfoIds, setPinnedInfoIds] = useState(() => new Set());
  const scrollRootRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listMetahumanCharacterOptions()
      .then((payload) => {
        if (cancelled) return;
        setOptions(Array.isArray(payload?.options) ? payload.options : []);
        setLoadError('');
      })
      .catch((err) => {
        if (cancelled) return;
        setOptions([]);
        setLoadError(err.message || 'Character options could not be loaded.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = asText(value);

  const filteredOptions = useMemo(
    () => filterCharacterOptions(options, search),
    [options, search]
  );

  const selectedOption = useMemo(
    () => options.find((option) => String(option.characterId) === selected) || null,
    [options, selected]
  );

  const toggleInfoPin = (characterId) => {
    setPinnedInfoIds((prev) => {
      const next = new Set(prev);
      if (next.has(characterId)) next.delete(characterId);
      else next.add(characterId);
      return next;
    });
  };

  useEffect(() => {
    scrollRootRef.current?.scrollTo({ top: 0 });
  }, [search]);

  return (
    <Box>
      <SelectedCharacterPreview
        option={selectedOption}
        onClear={() => onChange('')}
      />

      <TextField
        fullWidth
        size="small"
        label="Search characters"
        placeholder="Name, sex, age, race, size, BMI, idle animations…"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {!loading && loadError && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {loadError}
        </Typography>
      )}
      {!loading && !loadError && options.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No characters are available yet.
        </Typography>
      )}
      {!loading && options.length > 0 && filteredOptions.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No characters match your search.
        </Typography>
      )}
      {!loading && filteredOptions.length > 0 && (
        <Box
          ref={scrollRootRef}
          sx={{
            maxHeight: { xs: 720, sm: 880 },
            overflow: 'auto',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            pr: 0.5,
          }}
        >
          <ImageList
            cols={4}
            gap={8}
            sx={{
              m: 0,
              p: 1,
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr) !important',
                md: 'repeat(4, 1fr) !important',
              },
            }}
          >
            {filteredOptions.map((option) => (
              <CharacterTile
                key={option.characterId}
                option={option}
                selected={selected}
                onSelect={onChange}
                scrollRootRef={scrollRootRef}
                pinnedInfoIds={pinnedInfoIds}
                onToggleInfoPin={toggleInfoPin}
              />
            ))}
          </ImageList>
        </Box>
      )}
      {error ? (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
}
